import React, { useState, useRef, useEffect, useCallback } from 'react';

const ExportShareDropdown = ({ 
  onExportPDF, 
  onExportExcel, 
  onExportCSV,
  onExportAll,
  onSharePDF, 
  onShareExcel, 
  onShareImage, 
  onShareLink, 
  disabled = false,
  className = "bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-3 py-1.5 rounded text-xs transition duration-300 relative",
  showExportOptions = true,
  showShareOptions = true,
  exportFormat = 'CSV'
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const lastClickTime = useRef(0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close dropdown on ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDropdown]);

  const handleButtonClick = useCallback(() => {
    const now = Date.now();
    if (now - lastClickTime.current < 300) return; // Prevent rapid clicks
    lastClickTime.current = now;
    setShowDropdown(!showDropdown);
  }, [showDropdown]);

  const handleOptionClick = useCallback(async (action) => {
    if (!action || isProcessing) return;
    
    const now = Date.now();
    if (now - lastClickTime.current < 500) return; // Prevent rapid option clicks
    lastClickTime.current = now;
    
    setShowDropdown(false);
    setIsProcessing(true);
    
    try {
      if (typeof action === 'function') {
        await action();
      }
    } catch (error) {
      console.error('Export/Share action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing]);

  const exportOptions = [
    { 
      label: '📊 Export CSV', 
      action: onExportCSV,
      disabled: !onExportCSV
    },
    { 
      label: '📄 Export PDF', 
      action: onExportPDF,
      disabled: !onExportPDF
    },
    { 
      label: '📊 Export Excel', 
      action: onExportExcel,
      disabled: !onExportExcel
    },
    { 
      label: '📋 Export All', 
      action: onExportAll,
      disabled: !onExportAll
    }
  ].filter(option => !option.disabled);

  const shareOptions = [
    { 
      label: '📄 Share as PDF', 
      action: onSharePDF,
      disabled: !onSharePDF
    },
    { 
      label: '📊 Share as Excel', 
      action: onShareExcel,
      disabled: !onShareExcel
    },
    { 
      label: '🖼️ Share as Image', 
      action: onShareImage,
      disabled: !onShareImage
    }
  ].filter(option => !option.disabled);

  const allOptions = [
    ...(showExportOptions ? exportOptions : []),
    ...(showExportOptions && showShareOptions ? [{ label: '---', action: null, disabled: true, separator: true }] : []),
    ...(showShareOptions ? shareOptions : [])
  ];

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        disabled={disabled || isProcessing}
        className={className}
      >
        {isProcessing ? '⏳' : '⚙️'} Actions
      </button>
      
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200"
          style={{ zIndex: 9999 }}
        >
          {allOptions.length === 0 ? (
            <div className="p-2 text-gray-500 text-xs">No options available</div>
          ) : (
            allOptions.map((option, index) => (
              option.separator ? (
                <div key={index} className="border-t border-gray-200 my-1"></div>
              ) : (
                <button
                  key={index}
                  onClick={() => handleOptionClick(option.action)}
                  disabled={option.disabled || isProcessing}
                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {option.label}
                </button>
              )
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ExportShareDropdown;
